import { Cached } from "@coris-ts/helpers/utilities"
import { type ApiRequestService } from "@coris-ts/repository/data-source/api-request.service"
import {
  provideDefaultArrayNetworkDataSource,
  provideDefaultNetworkDataSource,
} from "@coris-ts/repository/data-source/network.data-source"
import { GetInteractor } from "@coris-ts/repository/interactor/get.interactor"
import { PutInteractor } from "@coris-ts/repository/interactor/put.interactor"
import { ArrayMapper, VoidMapper } from "@coris-ts/repository/mapper/mapper"
import { GetRepositoryMapper, RepositoryMapper } from "@coris-ts/repository/repository-mapper"
import {
  SingleDataSourceRepository,
  SingleGetDataSourceRepository,
} from "@coris-ts/repository/single-data-source.repository"
import { provideVoidNetworkDataSourceRepository } from "@coris-ts/repository/single-void-data-source.repository"
import { OfferingEntity } from "./data/entities/offering.entity"
import { SubscriptionEntity } from "./data/entities/subscription.entity"
import { GetOfferingInteractor } from "./domain/interactors/get-offering.interactor"
import { GetSubscriptionsInteractor } from "./domain/interactors/get-subscriptions.interactor"
import { MetaAdsInteractor } from "../meta-ads/domain/interactors/meta-ads.interactor"
import {
  OfferingEntityToOfferingMapper,
  OfferingProductEntityToOfferingProductMapper,
} from "./domain/mappers/offering.mappers"
import { SubscriptionEntityToSubscriptionMapper } from "./domain/mappers/subscription.mappers"
import { type Offering } from "./domain/models/offering"
import { type Subscription } from "./domain/models/subscription"

export interface PaymentComponent {
  provideGetOffering: () => GetOfferingInteractor
  provideGetSubscriptions: () => GetSubscriptionsInteractor
  provideMetaAds: () => MetaAdsInteractor
}

export class PaymentDefaultModule implements PaymentComponent {
  constructor(private readonly apiRequestService: ApiRequestService) {
  }

  @Cached()
  private getOfferingRepository(): GetRepositoryMapper<OfferingEntity, Offering> {
    const dataSource = provideDefaultNetworkDataSource(this.apiRequestService, OfferingEntity)

    const repository = new SingleGetDataSourceRepository(dataSource)

    return new GetRepositoryMapper(repository, new OfferingEntityToOfferingMapper())
  }

  @Cached()
  private getSubscriptionsRepository(): RepositoryMapper<SubscriptionEntity[], Subscription[]> {
    const dataSource = provideDefaultArrayNetworkDataSource(
        this.apiRequestService,
        SubscriptionEntity
    )

    const repository = new SingleDataSourceRepository(dataSource, dataSource, dataSource)

    return new RepositoryMapper(
        repository,
        repository,
        repository,
        new ArrayMapper(
            new SubscriptionEntityToSubscriptionMapper(
                new OfferingProductEntityToOfferingProductMapper()
            )
        ),
        new VoidMapper()
    )
  }

  public provideGetOffering(): GetOfferingInteractor {
    return new GetOfferingInteractor(new GetInteractor<Offering>(this.getOfferingRepository()))
  }

  public provideGetSubscriptions(): GetSubscriptionsInteractor {
    return new GetSubscriptionsInteractor(
        new GetInteractor<Subscription[]>(this.getSubscriptionsRepository())
    )
  }

  public provideMetaAds(): MetaAdsInteractor {
    return new MetaAdsInteractor(new PutInteractor<void>(provideVoidNetworkDataSourceRepository(this.apiRequestService)))
  }
}
